/**
 * Studio lighting path — a faithful port of configurator/tools/studio (the tuning
 * harness Alex dials recipes in). OPT-IN per product (viewer.studioLighting); the
 * legacy IBL + camera-headlight + ContactShadow path is untouched for every other
 * product (the Koala). What the studio shows = what this renders.
 *
 * Pieces (all driven by a StudioRecipe, swappable per colorway):
 *   - HDRI IBL (equirect .hdr through PMREM) at recipe.envIntensity.
 *   - Key / Fill / Rim directional lights, placed spherically (az/el/distance).
 *   - The KEY casts a real-time soft SELF-shadow (VSM) — cushion→seat, the thing
 *     model-viewer/legacy can't do. renderer.shadowMap is enabled while this path
 *     is active; the contact pass toggles it off for its own flat-depth render.
 *   - A depth-based ground CONTACT shadow with independent diffuse/darkness/offset
 *     /falloff controls (verbatim from the studio).
 *   - Per-material color GRADE (saturation/brightness/temperature) injected into the
 *     fabric shader after map_fragment.
 */

import * as THREE from "three";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import { HorizontalBlurShader } from "three/examples/jsm/shaders/HorizontalBlurShader.js";
import { VerticalBlurShader } from "three/examples/jsm/shaders/VerticalBlurShader.js";
import type { StudioRecipe, StudioLight, StudioRoom } from "@/types";

const TONE: Record<string, THREE.ToneMapping> = {
  ACES: THREE.ACESFilmicToneMapping,
  AgX: THREE.AgXToneMapping,
  Neutral: (THREE as unknown as { NeutralToneMapping?: THREE.ToneMapping }).NeutralToneMapping ?? THREE.ACESFilmicToneMapping,
  Reinhard: THREE.ReinhardToneMapping,
  Cineon: THREE.CineonToneMapping,
  Aucun: THREE.NoToneMapping,
};

function place(o: THREE.Object3D, az: number, el: number, d: number): void {
  const a = THREE.MathUtils.degToRad(az), e = THREE.MathUtils.degToRad(el);
  o.position.set(d * Math.cos(e) * Math.sin(a), d * Math.sin(e), d * Math.cos(e) * Math.cos(a));
}

export class StudioLighting {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private pmrem: THREE.PMREMGenerator;

  private key: THREE.DirectionalLight;
  private fill: THREE.DirectionalLight;
  private rim: THREE.DirectionalLight;
  private hemi: THREE.HemisphereLight;

  private recipe: StudioRecipe | null = null;
  private center = new THREE.Vector3();
  private size = new THREE.Vector3(1, 1, 1);
  /** Floor Y under the model (box.min.y). Lights are placed around the floor
   *  point under the model centre and aimed at 0.35×height — exactly the studio
   *  geometry, so the key angle + self-shadow match what Alex tuned. */
  private groundY = 0;
  private lastBox: THREE.Box3 | null = null;

  // env cache (url -> PMREM texture)
  private envCache = new Map<string, THREE.Texture>();
  private envUrlLoaded: string | null = null;

  // contact shadow (depth-based, independent controls)
  private csGroup: THREE.Group;
  private csRT: THREE.WebGLRenderTarget;
  private csRTBlur: THREE.WebGLRenderTarget;
  private csPlane: THREE.Mesh;
  private csPlaneMat: THREE.MeshBasicMaterial;
  private csBlurPlane: THREE.Mesh;
  private csCam: THREE.OrthographicCamera;
  private csDepthMat: THREE.MeshDepthMaterial;
  private csDarkness = { value: 1.6 };
  private csFalloff = { value: 1 };
  private hB: THREE.ShaderMaterial;
  private vB: THREE.ShaderMaterial;
  private readonly RT = 1024;

  private shadowOn = true;

  // Cast-shadow ground: a large plane that RECEIVES the key light's real VSM shadow,
  // so the sofa drops a soft, clearly-visible grounding shadow that extends past the
  // base (the silhouette-capture contact shadow is invisible under a floor-hugging
  // sofa). This is the primary floor shadow; opacity from recipe.gnd.opacity.
  private ground: THREE.Mesh;
  private groundMat: THREE.ShadowMaterial;

  // Room backdrop (recipe.room): inside-out wall box + an "infinite" white floor
  // so orbiting reads as a camera inside a real room. Hidden during the
  // contact-shadow depth capture — its floor sits at the capture camera's near
  // plane and would flood the RT black.
  private roomGroup: THREE.Group | null = null;
  private roomMats: THREE.Material[] = [];
  private roomGeos: THREE.BufferGeometry[] = [];
  private prevSceneBg: THREE.Color | THREE.Texture | null = null;
  private roomBgApplied = false;
  private roomBg: THREE.Color | null = null;

  constructor(scene: THREE.Scene, renderer: THREE.WebGLRenderer, camera: THREE.Camera) {
    this.scene = scene;
    this.renderer = renderer;
    this.pmrem = new THREE.PMREMGenerator(renderer);
    this.pmrem.compileEquirectangularShader();
    void camera;

    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.VSMShadowMap;

    this.key = new THREE.DirectionalLight(0xfff2e4, 1);
    this.key.castShadow = true;
    this.key.shadow.mapSize.set(2048, 2048);
    this.key.shadow.blurSamples = 24;
    this.key.shadow.bias = -0.0005;
    this.key.shadow.camera.near = 0.1;
    this.key.shadow.camera.far = 40;
    this.fill = new THREE.DirectionalLight(0xeaf0ff, 0);
    this.rim = new THREE.DirectionalLight(0xffffff, 0);
    this.hemi = new THREE.HemisphereLight(0xfbf7f0, 0xa89f90, 0);
    scene.add(this.key, this.key.target, this.fill, this.fill.target, this.rim, this.rim.target, this.hemi);

    // contact shadow rig
    this.csGroup = new THREE.Group();
    this.csGroup.name = "studio-contact-shadow";
    scene.add(this.csGroup);
    this.csRT = new THREE.WebGLRenderTarget(this.RT, this.RT);
    this.csRT.texture.generateMipmaps = false;
    this.csRTBlur = new THREE.WebGLRenderTarget(this.RT, this.RT);
    this.csRTBlur.texture.generateMipmaps = false;
    const geo = new THREE.PlaneGeometry(1, 1).rotateX(Math.PI / 2);
    this.csPlaneMat = new THREE.MeshBasicMaterial({ map: this.csRT.texture, transparent: true, opacity: 0.4, depthWrite: false, toneMapped: false });
    this.csPlaneMat.onBeforeCompile = (sh) => {
      sh.uniforms.uFalloff = this.csFalloff;
      sh.fragmentShader = "uniform float uFalloff;\n" + sh.fragmentShader.replace(
        "#include <map_fragment>",
        `#include <map_fragment>
         vec2 dd = abs(vMapUv - 0.5) * 2.0;
         float ef = (1.0 - smoothstep(0.45, 0.98, dd.x)) * (1.0 - smoothstep(0.45, 0.98, dd.y));
         diffuseColor.a *= mix(1.0, ef, uFalloff);`);
    };
    this.csPlane = new THREE.Mesh(geo, this.csPlaneMat);
    this.csPlane.renderOrder = 2;
    this.csPlane.scale.y = -1;
    this.csPlane.position.y = 0.002;
    this.csBlurPlane = new THREE.Mesh(geo.clone());
    this.csBlurPlane.visible = false;
    this.csCam = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 2);
    this.csCam.rotation.x = Math.PI / 2;
    this.csGroup.add(this.csPlane, this.csBlurPlane, this.csCam);

    this.csDepthMat = new THREE.MeshDepthMaterial();
    this.csDepthMat.onBeforeCompile = (s) => {
      s.uniforms.darkness = this.csDarkness;
      s.fragmentShader = "uniform float darkness;\n" + s.fragmentShader.replace(
        "gl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );",
        "gl_FragColor = vec4( vec3( 0.0 ), ( 1.0 - fragCoordZ ) * darkness );");
    };
    this.csDepthMat.depthTest = false;
    this.csDepthMat.depthWrite = false;
    this.hB = new THREE.ShaderMaterial(HorizontalBlurShader); this.hB.depthTest = false;
    this.vB = new THREE.ShaderMaterial(VerticalBlurShader); this.vB.depthTest = false;

    // cast-shadow ground (sized/placed in fit)
    this.groundMat = new THREE.ShadowMaterial({ opacity: 0.4, transparent: true });
    this.ground = new THREE.Mesh(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2), this.groundMat);
    this.ground.receiveShadow = true;
    this.ground.renderOrder = 0;
    scene.add(this.ground);
  }

  /** Attach color-grade hook + shadow flags to the assembly's fabric meshes. Call after rebuild. */
  setAssembly(group: THREE.Object3D): void {
    group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const m of mats) {
        const mat = m as THREE.MeshStandardMaterial | undefined;
        if (!mat) continue;
        if (mat.userData.isLeg) continue;
        const isFabric = mat.userData.isFabric || mesh.userData.isFabric;
        if (!isFabric) continue;
        if (!mat.userData.gradeUniforms) {
          const u = { uSat: { value: 1 }, uBright: { value: 1 }, uTemp: { value: 0 } };
          mat.userData.gradeUniforms = u;
          mat.onBeforeCompile = (sh) => {
            sh.uniforms.uSat = u.uSat; sh.uniforms.uBright = u.uBright; sh.uniforms.uTemp = u.uTemp;
            sh.fragmentShader = "uniform float uSat,uBright,uTemp;\n" + sh.fragmentShader.replace(
              "#include <map_fragment>",
              `#include <map_fragment>
               diffuseColor.rgb *= uBright;
               diffuseColor.rgb *= vec3(1.0+uTemp*0.16, 1.0, 1.0-uTemp*0.16);
               float _L = dot(diffuseColor.rgb, vec3(0.2126,0.7152,0.0722));
               diffuseColor.rgb = clamp(mix(vec3(_L), diffuseColor.rgb, uSat), 0.0, 1.0);`);
          };
          mat.needsUpdate = true;
        }
      }
    });
    if (this.recipe) this.applyGrade();
  }

  /** Farthest camera dolly-out, fed by the Engine after framing. Persisted across
   *  recipe re-fits (applyRecipe re-calls fit without it). */
  private maxCamDist = 0;

  /** Size the lights + contact shadow to a model bounding box. Call after rebuild. */
  fit(box: THREE.Box3, maxCamDist?: number): void {
    if (box.isEmpty()) return;
    if (maxCamDist !== undefined) this.maxCamDist = maxCamDist;
    this.lastBox = box.clone();
    box.getCenter(this.center);
    box.getSize(this.size);
    this.groundY = box.min.y;
    if (this.recipe) this.syncLights();

    const sc = this.recipe?.gnd.scale ?? 1.4;
    const W = Math.max(this.size.x, 0.2) * sc, H = Math.max(this.size.z, 0.2) * sc;
    const modelH = Math.max(this.size.y, 0.2);
    const ng = new THREE.PlaneGeometry(W, H).rotateX(Math.PI / 2);
    this.csPlane.geometry.dispose(); this.csPlane.geometry = ng;
    this.csBlurPlane.geometry = ng;
    this.csCam.left = -W / 2; this.csCam.right = W / 2; this.csCam.top = H / 2; this.csCam.bottom = -H / 2;
    this.csCam.near = 0; this.csCam.far = modelH * 1.4 + 0.1;
    // Camera STAYS at the group origin (the floor) looking straight UP (rotation.x
    // = +PI/2, set in ctor) so it captures the model's footprint with the darkest
    // band at the floor-contact line. Raising it above the model (the earlier bug)
    // made it look up into empty space → blank render target → no shadow.
    this.csCam.updateProjectionMatrix();
    this.csGroup.position.set(this.center.x, box.min.y, this.center.z);

    // size + place the cast-shadow ground just under the model
    const gs = Math.max(this.size.x, this.size.z) * 5 + 2;
    this.ground.geometry.dispose();
    this.ground.geometry = new THREE.PlaneGeometry(gs, gs).rotateX(-Math.PI / 2);
    this.ground.position.set(this.center.x, box.min.y + 0.001, this.center.z);

    this.buildRoom();
  }

  /** (Re)build the room backdrop around the current model box. Sized from the
   *  model footprint + clearance so every shape sits in a plausible room.
   *
   *  Spec (Alex 2026-06-10): ONE white floor that never ends (a huge plane, so
   *  zooming past the walls can never expose a "second floor" of background),
   *  walls almost indistinguishable from the floor, and the arêtes (wall/wall,
   *  floor/wall) reading as a slight value step only. The surfaces are UNLIT
   *  (MeshBasicMaterial, toneMapped:false): exact hex values, so the steps
   *  between faces are controlled to the percent and never blow out or go gray
   *  under the per-colorway lighting recipes. */
  private buildRoom(): void {
    if (this.roomGroup) {
      this.scene.remove(this.roomGroup);
      for (const g of this.roomGeos) g.dispose();
      for (const m of this.roomMats) m.dispose();
      this.roomGroup = null;
      this.roomMats = [];
      this.roomGeos = [];
    }
    const spec: StudioRoom | undefined = this.recipe?.room;
    if (!spec || spec.on === false || !this.lastBox) {
      if (this.roomBgApplied) { this.scene.background = this.prevSceneBg; this.roomBgApplied = false; }
      return;
    }
    // Half-extent: the configured clearance is a floor, but the walls ALWAYS sit
    // beyond the camera's farthest dolly-out (+15%), so zooming all the way out
    // can never put the camera through (or against) a wall.
    const clear = spec.clearance ?? 1.8;
    const half = Math.max(
      Math.max(this.size.x, this.size.z) / 2 + clear,
      this.maxCamDist * 1.15,
    );
    const W = half * 2;
    const D = half * 2;
    // A huge room with a 2.8m ceiling reads as a parking garage — by default the
    // height grows with the footprint (a showroom). An explicit recipe height wins.
    const H = spec.height ?? Math.max(2.8, half * 0.35);

    const floorCol = spec.floor ?? "#ffffff";
    const wallCol = new THREE.Color(spec.wall ?? "#f5f4f1");
    // The two wall pairs get a ±1.5% value split so the vertical wall/wall
    // arêtes stay readable even though all faces are flat-shaded near-white.
    const wallA = wallCol.clone().multiplyScalar(1.015); // ±z walls (face the default camera)
    const wallB = wallCol.clone().multiplyScalar(0.985); // ±x walls
    const ceilCol = spec.ceiling ? new THREE.Color(spec.ceiling) : wallCol.clone().multiplyScalar(0.97);

    const mk = (col: THREE.Color | string, side: THREE.Side) => {
      const m = new THREE.MeshBasicMaterial({ color: col, side });
      m.toneMapped = false; // exact value out — the recipes' exposure must not shift the room
      return m;
    };
    const matFloor = mk(floorCol, THREE.FrontSide);
    const matWallA = mk(wallA, THREE.BackSide);
    const matWallB = mk(wallB, THREE.BackSide);
    const matCeil = mk(ceilCol, THREE.BackSide);
    const matHidden = new THREE.MeshBasicMaterial({ visible: false });
    this.roomMats = [matFloor, matWallA, matWallB, matCeil, matHidden];

    const group = new THREE.Group();
    group.name = "studio-room";

    // Transparent studio (2026-06-13): NO opaque floor plane and NO walls. An opaque floor
    // plane at ground level OCCLUDED the contact shadow. Instead the canvas is transparent
    // (renderer alpha:true) and shows the host --nv-bg as the floor color; the contact shadow
    // (csPlane) composites directly onto it. floor color is decided in CSS (--nv-bg).
    void W; void H; void D; void floorCol; void matFloor; void matWallA; void matWallB; void matCeil; void matHidden;
    this.roomGeos = [];
    this.roomGroup = group;
    this.scene.add(group);

    // Transparent scene background (no opaque colored bg). roomBg=null so render()'s
    // per-frame re-assert short-circuits and never repaints an opaque background.
    if (!this.roomBgApplied) { this.prevSceneBg = this.scene.background as THREE.Color | THREE.Texture | null; this.roomBgApplied = true; }
    this.roomBg = null;
    this.scene.background = null;
  }

  /** Apply a recipe (lights, env, tone/exposure, contact shadow, grade). Live-safe. */
  applyRecipe(recipe: StudioRecipe): void {
    this.recipe = recipe;
    // tone mapping + exposure
    this.renderer.toneMapping = TONE[recipe.toneMapping ?? "ACES"] ?? THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = recipe.exposure ?? 1.0;
    // env
    this.applyEnv(recipe);
    // lights
    this.syncLights();
    // floor shadow = the studio's silhouette-capture contact shadow (exactly what
    // Alex tuned). The cast-shadow ground is disabled.
    this.ground.visible = false;
    this.shadowOn = recipe.gnd.on !== false;
    this.csPlane.visible = this.shadowOn;
    this.csPlaneMat.opacity = recipe.gnd.opacity;
    this.csDarkness.value = recipe.gnd.darkness;
    this.csFalloff.value = recipe.gnd.falloff ? 1 : 0;
    // grade
    this.applyGrade();
    // Re-fit so gnd.scale (and the light placement, via syncLights) reflect THIS
    // recipe — fit() reads this.recipe, now set above. Without this the contact
    // shadow keeps the previous/default footprint scale.
    if (this.lastBox) this.fit(this.lastBox);
    else this.syncLights();
  }

  private applyEnv(recipe: StudioRecipe): void {
    this.scene.environmentIntensity = recipe.envIntensity ?? 0.5;
    if (recipe.envOn === false || !recipe.envHdri) { this.scene.environment = null; return; }
    const url = recipe.envHdri;
    const cached = this.envCache.get(url);
    if (cached) { this.scene.environment = cached; this.envUrlLoaded = url; return; }
    if (this.envUrlLoaded === url) return;
    this.envUrlLoaded = url;
    new RGBELoader().load(url, (h) => {
      h.mapping = THREE.EquirectangularReflectionMapping;
      const tex = this.pmrem.fromEquirectangular(h).texture;
      h.dispose();
      this.envCache.set(url, tex);
      if (this.recipe === recipe && recipe.envOn !== false) this.scene.environment = tex;
    });
  }

  private setLight(light: THREE.DirectionalLight, spec: StudioLight | undefined): void {
    if (!spec) { light.intensity = 0; return; }
    const D = Math.max(this.size.x, this.size.z);
    place(light, spec.az, spec.el, D * spec.d / 4);
    // studio geometry: around the floor point under the model centre, aimed at
    // 0.35×height (NOT the bbox centre) — matches tools/studio exactly.
    light.position.x += this.center.x;
    light.position.y += this.groundY;
    light.position.z += this.center.z;
    light.target.position.set(this.center.x, this.groundY + this.size.y * 0.35, this.center.z);
    light.intensity = spec.on ? spec.int : 0;
    light.color.set(spec.col);
  }

  private syncLights(): void {
    const r = this.recipe;
    if (!r) return;
    this.setLight(this.key, r.key);
    this.setLight(this.fill, r.fill);
    this.setLight(this.rim, r.rim);
    // key self-shadow
    this.key.castShadow = r.key.selfShadow !== false;
    this.key.shadow.radius = r.key.soft ?? 4;
    // cover the model + a margin so the cast shadow's penumbra lands on the ground
    const half = Math.max(this.size.x, this.size.y, this.size.z) * 1.15 + 0.6;
    const cam = this.key.shadow.camera;
    cam.left = -half; cam.right = half; cam.top = half; cam.bottom = -half;
    cam.updateProjectionMatrix();
    this.hemi.intensity = r.hemi ?? 0;
  }

  private applyGrade(): void {
    const g = this.recipe?.grade ?? { sat: 1, bright: 1, temp: 0 };
    this.scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const m of mats) {
        const u = (m as THREE.Material | undefined)?.userData?.gradeUniforms as
          | { uSat: { value: number }; uBright: { value: number }; uTemp: { value: number } } | undefined;
        if (!u) continue;
        u.uSat.value = g.sat; u.uBright.value = g.bright; u.uTemp.value = g.temp;
      }
    });
  }

  /** Per-frame: capture + blur the ground contact shadow. Call before the main render. */
  render(): void {
    // The room owns the canvas background: theme code (Engine.setBackground via
    // the mobile UI) can run AFTER buildRoom and repaint it — a cream band then
    // shows over the wall tops (the cover-generator regression, 2026-06-10).
    // Re-asserting per frame costs a pointer compare and always wins.
    if (this.roomGroup && this.roomBg && this.scene.background !== this.roomBg) {
      this.scene.background = this.roomBg;
    }
    if (!this.shadowOn || !this.recipe) return;
    const renderer = this.renderer, scene = this.scene;
    const prevBg = scene.background, prevEnv = scene.environment, prevEnvI = scene.environmentIntensity;
    const prevOverride = scene.overrideMaterial, prevRT = renderer.getRenderTarget();
    const prevShadow = renderer.shadowMap.enabled;
    const prevClear = new THREE.Color(); renderer.getClearColor(prevClear); const prevAlpha = renderer.getClearAlpha();

    // shadow falls away from the key light (asymmetry)
    const off = this.recipe.gnd.offset ?? 0;
    const dir = this.key.position.clone().sub(this.center).normalize();
    const D = Math.max(this.size.x, this.size.z);
    this.csGroup.position.x = this.center.x - dir.x * off * D;
    this.csGroup.position.z = this.center.z - dir.z * off * D;

    scene.background = null; scene.environment = null; scene.environmentIntensity = 0;
    renderer.shadowMap.enabled = false;
    renderer.setClearColor(0x000000, 0);
    const planeVis = this.csPlane.visible; this.csPlane.visible = false;
    const roomVis = this.roomGroup?.visible ?? false;
    if (this.roomGroup) this.roomGroup.visible = false;
    scene.overrideMaterial = this.csDepthMat;
    renderer.setRenderTarget(this.csRT); renderer.clear(); renderer.render(scene, this.csCam);
    scene.overrideMaterial = prevOverride;
    const b = (this.recipe.gnd.blur ?? 4) / this.RT;
    this.blur(this.csRT, this.csRTBlur, b, this.hB, "h");
    this.blur(this.csRTBlur, this.csRT, b, this.vB, "v");
    this.blur(this.csRT, this.csRTBlur, b * 0.4, this.hB, "h");
    this.blur(this.csRTBlur, this.csRT, b * 0.4, this.vB, "v");

    this.csPlane.visible = planeVis;
    if (this.roomGroup) this.roomGroup.visible = roomVis;
    scene.background = prevBg; scene.environment = prevEnv; scene.environmentIntensity = prevEnvI;
    renderer.shadowMap.enabled = prevShadow;
    renderer.setRenderTarget(prevRT);
    renderer.setClearColor(prevClear, prevAlpha);
  }

  private blur(from: THREE.WebGLRenderTarget, to: THREE.WebGLRenderTarget, amount: number, mat: THREE.ShaderMaterial, axis: "h" | "v"): void {
    mat.uniforms.tDiffuse.value = from.texture;
    if (axis === "h") mat.uniforms.h.value = amount; else mat.uniforms.v.value = amount;
    this.csBlurPlane.material = mat; this.csBlurPlane.visible = true;
    this.renderer.setRenderTarget(to); this.renderer.clear(); this.renderer.render(this.csBlurPlane, this.csCam);
    this.csBlurPlane.visible = false;
  }

  dispose(): void {
    this.scene.remove(this.key, this.key.target, this.fill, this.fill.target, this.rim, this.rim.target, this.hemi, this.csGroup, this.ground);
    if (this.roomGroup) {
      this.scene.remove(this.roomGroup);
      for (const g of this.roomGeos) g.dispose();
      for (const m of this.roomMats) m.dispose();
      this.roomGroup = null;
      this.roomMats = [];
      this.roomGeos = [];
      if (this.roomBgApplied) { this.scene.background = this.prevSceneBg; this.roomBgApplied = false; }
    }
    this.ground.geometry.dispose(); this.groundMat.dispose();
    this.csRT.dispose(); this.csRTBlur.dispose(); this.csPlaneMat.dispose(); this.csDepthMat.dispose();
    this.hB.dispose(); this.vB.dispose(); this.pmrem.dispose();
    for (const t of this.envCache.values()) t.dispose();
    this.envCache.clear();
    this.renderer.shadowMap.enabled = false;
  }
}
